/** История документа: auditEvents → UI text/visual mapping (spec §5). */
import { useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../../i18n'
import { timeAgo } from '../../../lib/format'
import { tone } from '../../../theme/tones'
import { Icon } from '../../../ui'
import { pressable } from '../../../ui/primitives'
import type { EdoAuditEvent } from '../../../lib/types'
import { CONTRACT_ENUM_LABEL, REVISED_FIELD_LABEL } from '../../../lib/edo-labels'
import { fmtDateTime } from './CardDetails'

/** Код события аудита — источник списка один: `EdoAuditEvent.event` в types.ts. */
export type AuditEventCode = EdoAuditEvent['event']

/**
 * Подписи кодов `EdoAuditEvent.event`. Ключ — union из types.ts, а не `string`: словарь обязан
 * покрывать ВСЕ коды, которые пишет бэк, иначе событие рендерится сырой латиницей (фолбэк ниже).
 * До 13.08.2026 так и было — знали 8 кодов из 20, и пользователь видел `claimed`/`revised`.
 * Теперь пропуск — ошибка компиляции: добавили код на бэке → расширьте union → допишите строку.
 */
export const AUDIT_TEXT: Record<AuditEventCode, Localized> = {
  termination_draft_withdrawn: { ru: 'Черновик увольнения отозван в портале', en: 'Termination draft withdrawn in the portal', az: 'İşdən çıxma qaralaması portalda geri götürüldü' },
  termination_draft_result: { ru: 'Черновик приказа увольнения создан в 1С', en: 'Termination order draft created in 1C', az: 'İşdən çıxma əmrinin qaralaması 1C-də yaradıldı' },
  termination_posting_confirmed: { ru: 'Проведение увольнения подтверждено по кадровой истории 1С', en: 'Termination posting confirmed by 1C employment history', az: 'İşdən çıxmanın təsdiqi 1C kadr tarixçəsi ilə yoxlanıldı' },
  termination_submitted: { ru: 'Заявка подана на согласование', en: 'Submitted for approval', az: 'Razılaşdırmaya təqdim edildi' },
  termination_completed: { ru: 'Приказ подписан, скан загружен', en: 'Order signed and scanned', az: 'Əmr imzalandı, skan yükləndi' },
  sla_reminder: { ru: 'Срок шага истёк — напоминание исполнителю', en: 'Step deadline passed — reminder sent', az: 'Addımın müddəti bitdi — xatırlatma göndərildi' },
  created: { ru: 'Заявка создана', en: 'Request created' },
  route: { ru: 'Передано на следующий шаг', en: 'Routed to next step' },
  decision: { ru: 'Решение по шагу', en: 'Step decision' },
  revised: { ru: 'Документ исправлен на доработке', en: 'Document revised after rework' },
  claimed: { ru: 'Задача взята в работу', en: 'Task taken' },
  unclaimed: { ru: 'Задача возвращена в группу', en: 'Task returned to the group' },
  assigned: { ru: 'Задача назначена', en: 'Task assigned' },
  reassigned: { ru: 'Задача переназначена', en: 'Task reassigned' },
  escalation: { ru: 'Таймер 3 ч истёк — эскалация', en: '3h timer elapsed — escalated' },
  approved: { ru: 'Согласовано всеми участниками', en: 'Approved by all participants' },
  accrued: { ru: 'Начисление подтверждено', en: 'Accrual confirmed' },
  posted: { ru: 'Проведено в 1С', en: 'Posted to 1C' },
  post_failed: { ru: 'Сбой проводки в 1С', en: '1C posting failed' },
  // Таймаут записи: документ в 1С мог создаться — текст ведёт к сверке, а не к повтору
  // (повтор без сверки даёт второй документ, дедупа по message_id у 1С нет).
  post_unverified: { ru: 'Исход проводки неизвестен — сверьте в 1С', en: 'Posting outcome unknown — check in 1C' },
  post_sync_stuck: { ru: 'Проводка прошла, следующий шаг не создан', en: 'Posted, but the next step was not created' },
  repost_requested: { ru: 'Запрошена повторная проводка', en: 'Re-posting requested' },
  registered: { ru: 'Зарегистрировано (приказ создан)', en: 'Registered (order created)' },
  closed: { ru: 'Вакансия закрыта (кандидат найден)', en: 'Vacancy closed (candidate hired)' },
  rejected: { ru: 'Отклонено', en: 'Rejected' },
  cancelled: { ru: 'Отозвано', en: 'Withdrawn' },
  approver_added: { ru: 'Добавлен согласующий', en: 'Approver added' },
  order_scans_updated: { ru: 'Приказы изменены (HR)', en: 'Orders updated (HR)' },
  // Разовое дозаполнение реквизитов зарубежного контрагента в заявках до 19.10.2026 (скрипт админа).
  requisites_filled: { ru: 'Дописаны реквизиты контрагента', en: 'Counterparty details added' },
  // IT-работы (ТЗ-2026-АКТ001). Итог этапа (согласовано / на доработку) — в комментарии бэка под заголовком.
  submitted: { ru: 'Подано на согласование', en: 'Submitted for approval', az: 'Razılaşdırmaya təqdim edildi' },
  it_modules_closed: { ru: 'Этап модулей закрыт', en: 'Module stage closed', az: 'Modul mərhələsi bağlandı' },
  // Пишет любой групповой шаг без кандидатов; карточка в это время показывает красную строку.
  no_executor: { ru: 'Шаг без исполнителя', en: 'Step has no assignee', az: 'Addımın icraçısı yoxdur' },
}
export const AUDIT_VIS: Record<AuditEventCode, { tone: string; icon: string }> = {
  termination_draft_withdrawn: { tone: 'default', icon: 'x' },
  termination_draft_result: { tone: 'gold', icon: 'clock' },
  termination_posting_confirmed: { tone: 'green', icon: 'check-circle' },
  termination_submitted: { tone: 'blue', icon: 'send' },
  termination_completed: { tone: 'green', icon: 'check-circle' },
  sla_reminder: { tone: 'gold', icon: 'clock' },
  created: { tone: 'blue', icon: 'plus' },
  route: { tone: 'blue', icon: 'arrow-right' },
  decision: { tone: 'green', icon: 'check' },
  revised: { tone: 'blue', icon: 'pencil' },
  claimed: { tone: 'blue', icon: 'user-plus' },
  unclaimed: { tone: 'gold', icon: 'user-minus' },
  assigned: { tone: 'blue', icon: 'user-plus' },
  reassigned: { tone: 'gold', icon: 'user-plus' },
  escalation: { tone: 'gold', icon: 'triangle-alert' },
  approved: { tone: 'green', icon: 'check' },
  accrued: { tone: 'green', icon: 'check' },
  posted: { tone: 'green', icon: 'check-circle' },
  post_failed: { tone: 'red', icon: 'x' },
  // Не красный: исход НЕизвестен, «провалено» толкает HR на повторную проводку.
  post_unverified: { tone: 'gold', icon: 'triangle-alert' },
  post_sync_stuck: { tone: 'gold', icon: 'triangle-alert' },
  repost_requested: { tone: 'blue', icon: 'arrow-right' },
  registered: { tone: 'green', icon: 'check-circle' },
  closed: { tone: 'green', icon: 'check-circle' },
  rejected: { tone: 'red', icon: 'x' },
  cancelled: { tone: 'red', icon: 'x' },
  approver_added: { tone: 'blue', icon: 'user-plus' },
  order_scans_updated: { tone: 'blue', icon: 'paperclip' },
  requisites_filled: { tone: 'blue', icon: 'pencil' },
  submitted: { tone: 'blue', icon: 'send' },
  it_modules_closed: { tone: 'blue', icon: 'check-circle' },
  no_executor: { tone: 'red', icon: 'triangle-alert' },
}
// Фолбэки остаются: `event` на бэке — свободная строка, и более новый сервер может прислать код,
// которого в union'е ещё нет. Приводим к `string`-индексу осознанно — иначе TS считает попадание
// гарантированным, а оно не гарантировано ничем, кроме нашей же дисциплины.
const byCode = <T,>(dict: Record<AuditEventCode, T>, e: string): T | undefined =>
  (dict as Record<string, T>)[e]
const auditVis = (e: string) => byCode(AUDIT_VIS, e) ?? { tone: 'default', icon: 'circle' }

/** Поля-ссылки IT-работ: в диффе — cuid, людям он ничего не говорит; показываем только «изменено». */
const ID_KEYS = new Set(['contractorId', 'finalApproverId', 'invoiceUploadId'])

/**
 * Значение поля в диффе версии: enum-коды договора локализуем, пустое — прочерком. Списки и объекты
 * (отчёты, модули, получатели IT-работ) — сводкой: число элементов или «изменено», иначе
 * String() печатал «[object Object]». Поля-ссылки (ID_KEYS) — одним «изменено», без id.
 */
export function DiffRows({ diff }: { diff: NonNullable<EdoAuditEvent['diff']> }) {
  const { t } = useApp()
  const changed = t({ ru: 'изменено', en: 'changed', az: 'dəyişdirilib' })
  const fmt = (v: unknown): string => {
    if (Array.isArray(v)) return String(v.length)
    if (v && typeof v === 'object') return changed
    const s = v == null ? '' : String(v).trim()
    if (!s) return '—'
    const enumLabel = CONTRACT_ENUM_LABEL[s]
    return enumLabel ? t(enumLabel) : s
  }
  return (
    <YStack marginTop={4} gap={2}>
      {Object.entries(diff).map(([k, ch]) => (
        <Text key={k} fontSize={12} color="$muted" numberOfLines={4}>
          <Text fontSize={12} color="$muted" fontWeight="600">
            {t(REVISED_FIELD_LABEL[k] ?? { ru: k, en: k })}:
          </Text>{' '}
          {ID_KEYS.has(k) ? changed : `${fmt(ch.from)} → ${fmt(ch.to)}`}
        </Text>
      ))}
    </YStack>
  )
}

/**
 * Системные события — служебная трасса движка: переход на следующий шаг и взятие/возврат групповой
 * задачи. В истории скрыты по умолчанию (включает флажок): «Передано на следующий шаг» повторяет
 * маршрут. Бэк пишет их всегда с автокомментарием («На шаге: …», «Взял в работу: …»), поэтому
 * «без комментария» их не отличит — скрываем по коду. Всё прочее — эскалации, сбои проводки,
 * отказы, отзыв, решения с комментарием — видно всегда. Новый служебный код — дописать сюда.
 */
export const AUDIT_SYSTEM: ReadonlySet<AuditEventCode> = new Set<AuditEventCode>(['route', 'claimed', 'unclaimed'])
export const isSystemEvent = (ev: Pick<EdoAuditEvent, 'event'>) => (AUDIT_SYSTEM as ReadonlySet<string>).has(ev.event)

/**
 * История — компактная лента, новые сверху: точка тона события, заголовок, время (абсолютное;
 * относительное — в подсказке), под ним автор и комментарий. Системные события — под флажком.
 */
export function AuditTimeline({ events }: { events: EdoAuditEvent[] }) {
  const { t, l } = useApp()
  const [showSystem, setShowSystem] = useState(false)
  // Версия документа — по хронологии, до разворота ленты: v1 = подача, каждое revised = +1.
  let revisions = 0
  const versions = new Map(events.filter((e) => e.event === 'revised').map((e) => [e.id, ++revisions + 1]))
  const systemCount = events.filter(isSystemEvent).length
  const shown = events.filter((e) => showSystem || !isSystemEvent(e)).reverse()
  return (
    <YStack gap={6}>
      {systemCount ? (
        <XStack
          {...pressable}
          role="checkbox"
          aria-checked={showSystem}
          onPress={() => setShowSystem(!showSystem)}
          cursor="pointer"
          alignItems="center"
          gap={10}
          minHeight={44}
        >
          <Stack
            width={18}
            height={18}
            borderRadius={4}
            borderWidth={1}
            borderColor={showSystem ? '$primary' : '$borderColor'}
            backgroundColor={showSystem ? '$primary' : '$background'}
            alignItems="center"
            justifyContent="center"
          >
            {showSystem ? <Icon name="check" size={12} color="$onPrimary" /> : null}
          </Stack>
          <Text fontSize={13} color="$color">
            {l('card.showSystem').replace('{n}', String(systemCount))}
          </Text>
        </XStack>
      ) : null}
      {shown.length ? (
        <YStack>
          {shown.map((ev, i) => {
            const tn = tone(auditVis(ev.event).tone)
            const version = versions.get(ev.id)
            const who = ev.actor ? ev.actor.fullName : t({ ru: 'Система', en: 'System' })
            const label = byCode(AUDIT_TEXT, ev.event) ?? { ru: ev.event, en: ev.event }
            const title = t(label)
            // Комментарий бэка, повторяющий заголовок («Подано на согласование»), — второй раз не печатаем.
            const note = ev.comment && ev.comment !== (typeof label === 'string' ? label : label.ru) && t(ev.comment) !== title ? t(ev.comment) : ''
            return (
              <XStack key={ev.id} gap={10} paddingVertical={9} borderBottomWidth={i < shown.length - 1 ? 1 : 0} borderColor="$borderSecondary">
                <Stack width={8} height={8} flexShrink={0} borderRadius={999} marginTop={5} backgroundColor={tn.solid} />
                <YStack flex={1} minWidth={0} gap={2}>
                  <XStack alignItems="flex-start" justifyContent="space-between" gap={8}>
                    <XStack flexShrink={1} alignItems="center" gap={6} flexWrap="wrap">
                      <Text fontSize={13} fontWeight="600" color="$color">
                        {title}
                      </Text>
                      {version != null && (
                        <Stack backgroundColor={tn.soft} borderRadius={999} paddingHorizontal={8} paddingVertical={1}>
                          <Text fontSize={11} fontWeight="700" color={tn.text}>
                            {t({ ru: 'версия', en: 'version', az: 'versiya' })} {version}
                          </Text>
                        </Stack>
                      )}
                    </XStack>
                    {/* относительное время — во всплывающей подсказке (title нет в типах Tamagui, на вебе уходит в DOM) */}
                    <Text fontSize={11} color="$muted" flexShrink={0} {...({ title: t(timeAgo(ev.createdAt)) } as object)}>
                      {fmtDateTime(ev.createdAt)}
                    </Text>
                  </XStack>
                  <Text fontSize={12} color="$muted" lineHeight={17}>
                    {note ? `${who}: ${note}` : who}
                  </Text>
                  {ev.event === 'revised' && ev.diff ? <DiffRows diff={ev.diff} /> : null}
                </YStack>
              </XStack>
            )
          })}
        </YStack>
      ) : (
        <Text fontSize={13} color="$muted">
          {l('card.noEvents')}
        </Text>
      )}
    </YStack>
  )
}
